import { defineAbility, isOneOf, type Permission, type PermissionScope, parsePermission, scopeFor } from '@tnn/core'
import { subjects } from './registry'

/**
 * What every signed-in person may do whatever their roles (`loadMe` adds it to theirs): read the branches and the
 * company's details, which is what opens Company to all. The staff directory there is for anyone with an employee
 * record (`/api/company/people`), not a permission
 */
export const everyonePermissions: readonly Permission[] = [
  'company.branch:read',
  'company.profile:read',
  // The company's things in one's own hands: seen, and a lend handed to one confirmed (the server checks it is one's own)
  'company.asset:read@own',
  'company.assetHandover:read@own',
  'company.assetHandover:receive',
  'company.uniform:read@own',
  // An expense paid with one's own money, claimed back (the server takes claims from employees only) and cancelled until paid
  'cash.claim:read@own',
  'cash.claim:create',
  'cash.claim:update@own',
  'cash.claim:delete@own',
  'cash.claim:submit',
  'cash.claim:cancel@own',
  'cash.claim:print',
]

export interface RoleTemplate {
  /** What the database knows it by: renaming one is a migration's (the API's start removes a key the code no longer has) */
  key: string
  name: string
  description: string
  permissions: Permission[]
  /**
   * What a holder gets beside when they head a team (`Team`): their work's own deciding and undoing, as far as each
   * permission says (their branch's) — a head of a team in transport is a head of their branch
   */
  lead?: Permission[]
  /** Keys of the roles whose holders may give this one (`admin.role.grantableBy` by id); a super admin gives any */
  grantableBy: string[]
}

/**
 * Everyone who takes cash holds it (the cash module): sees their own ledger and security deposit,
 * sends collections on to anyone (into a safe: accounting only, `putsIntoSafes`), deposits them, pays petty expenses from their reserve, confirms
 * cash sent to them, returns reserve, and sees accounting's moves of their cash. Cancelling their own documents before
 * the other side confirms is theirs too; the server allows nothing else on other people's cash. A safe's custodian
 * counts cash in and deposits it by being named on the safe, not by a role.
 */
const cashHandler: Permission[] = [
  'cash.entry:read@own',
  'cash.transfer:read@own',
  'cash.transfer:create',
  'cash.transfer:update@own',
  'cash.transfer:delete@own',
  'cash.transfer:submit',
  'cash.transfer:cancel@own',
  'cash.transfer:receive',
  'cash.transfer:print',
  'cash.deposit:read@own',
  'cash.deposit:create',
  'cash.deposit:update@own',
  'cash.deposit:delete@own',
  'cash.deposit:submit',
  'cash.deposit:print',
  'cash.expense:read@own',
  'cash.expense:create',
  'cash.expense:update@own',
  'cash.expense:delete@own',
  'cash.expense:submit',
  'cash.expense:print',
  'cash.reserve:read@own',
  'cash.reserve:create',
  'cash.reserve:update@own',
  'cash.reserve:delete@own',
  'cash.reserve:submit',
  'cash.reserve:cancel@own',
  'cash.reserve:receive',
  'cash.reserve:print',
  // Accounting moved cash into or out of their hands
  'cash.move:read@own',
  // Their own security deposit paid in or used (accounting records it)
  'cash.bond:read@own',
]

/*
 * Someone without a phone tells the department concerned, which records it for them: their time and leave the head of
 * their team (`teamPermissions`), who fills in their OT anyway (ตาราง OT). Cash nobody records for anyone: whoever
 * presses the button holds it, and cash in the wrong hands accounting moves (`cash.move`).
 */

/**
 * What the head of a team does over it (`@team`: the people under them at work, all the way down, by
 * hr.employee.managerId), whatever their roles: no role gives it, being someone's manager does, so it follows the org
 * chart HR keeps. Their people's leave and time (decided, recorded for someone without a phone, OT hours on ตาราง OT,
 * clocked in at the counter), their salary advances (what may be advanced, never what they are paid), the account
 * requests of those already on the staff, and their cash: shortages and surpluses reviewed, documents cancelled, what
 * they claim back approved (accounting transfers it). HR does all of it for everyone
 */
export const teamPermissions: readonly Permission[] = [
  'hr.employee:read@team',
  'hr.department:read',
  'hr.position:read',
  'hr.schedule:read',
  'hr.holiday:read',
  'hr.leaveType:read',
  'hr.leave:read@team',
  'hr.leave:create@team',
  'hr.leave:update@team',
  'hr.leave:approve@team',
  'hr.leave:cancel@team',
  'hr.leave:recordFor@team',
  'hr.leave:print',
  'hr.leaveAllowance:read@team',
  'hr.overtime:read@team',
  'hr.overtime:record@team',
  'hr.overtime:export',
  'hr.timeEntry:read@team',
  'hr.timeEntry:export',
  'hr.timeEntry:recordFor@team',
  'hr.timeEntry:approve@team',
  // Clocking in or out someone of their team without a phone, standing at the counter (the server keeps to the team)
  'company.branch:clockFor',
  // Their people's salary advances (in Payroll): approve, hand over the cash, ask for someone without a phone
  'payroll.advance:read@team',
  'payroll.advance:approve@team',
  'payroll.advance:cancel@team',
  'payroll.advance:pay@team',
  'payroll.advance:recordFor@team',
  // Someone of the team asking for another app (a sign-up is their branch's: `joining`)
  'access.request:read@team',
  'access.request:approve@team',
  'cash.claim:read@team',
  'cash.claim:approve@team',
  'cash.claim:cancel@team',
  'cash.claim:export',
  'cash.transfer:approveVariance@team',
  'cash.entry:read@team',
  'cash.entry:export',
  'cash.transfer:read@team',
  'cash.transfer:cancel@team',
  'cash.transfer:export',
  'cash.deposit:read@team',
  'cash.deposit:cancel@team',
  'cash.deposit:export',
  'cash.expense:read@team',
  'cash.expense:cancel@team',
  'cash.expense:export',
  'cash.reserve:read@team',
]

/**
 * A branch's things (the asset register), looked after by its heads in transport: added, handed to people and taken
 * back, the uniforms given noted. What a loss costs is accounting's to decide (`review`)
 */
const assetLead: Permission[] = [
  'company.asset:read@branch',
  'company.asset:create@branch',
  'company.asset:update@branch',
  'company.asset:export',
  'company.assetHandover:read@branch',
  'company.assetHandover:create@branch',
  'company.assetHandover:update@branch',
  'company.assetHandover:delete@branch',
  'company.assetHandover:submit@branch',
  'company.assetHandover:cancel@branch',
  'company.assetHandover:amend@branch',
  'company.assetHandover:print',
  'company.assetHandover:export',
  'company.uniform:read@branch',
  'company.uniform:create@branch',
  'company.uniform:update@branch',
  'company.uniform:delete@branch',
  'company.uniform:export',
]

/**
 * Who joins at a branch, let in by its heads in transport: invite links (the position's roles), password reset links,
 * sign-ups with the branch's code, and job applications for someone of the branch without a phone (HR approves those)
 */
const joining: Permission[] = [
  'access.invite:read@branch',
  'access.invite:create@branch',
  'access.invite:revoke@branch',
  'access.invite:reset@branch',
  'access.request:read@branch',
  'access.request:approve@branch',
  'hr.application:read@branch',
  'hr.application:create@branch',
  'hr.application:update@branch',
]

/**
 * Moving the bills (เที่ยวรถ, รอบส่ง): a branch opens trips and delivery runs from itself, puts them up and writes what a
 * trip costs as a draft bill to accounting (`cost`), scans bills on and off trucks where it is (`scan@branch`: at
 * one's home branch: nobody in Bangkok takes bills in at Surat), records how a run's deliveries went for a driver
 * without a phone, and hands a bill over to its receiver at the counter. Trips and runs of every branch are there to
 * look at: trucks come in from them
 */
const branchMoves: Permission[] = [
  // The receiver collects a bill with nothing owed at the counter (at the bill's destination only)
  'tms.waybill:handover@branch',
  'tms.trip:read',
  'tms.trip:create',
  'tms.trip:update@branch',
  'tms.trip:delete@branch',
  'tms.trip:submit@branch',
  'tms.trip:scan@branch',
  'tms.trip:cost@branch',
  'tms.trip:print',
  'tms.trip:export',
  'tms.deliveryRun:read',
  'tms.deliveryRun:create',
  'tms.deliveryRun:update@branch',
  'tms.deliveryRun:delete@branch',
  'tms.deliveryRun:submit@branch',
  'tms.deliveryRun:scan@branch',
  'tms.deliveryRun:deliver@branch',
  'tms.deliveryRun:print',
  'tms.deliveryRun:export',
  // Made by whoever loads (their `scan`): read and reprinted
  'tms.manifest:read',
  'tms.manifest:print',
  'tms.manifest:export',
]

/**
 * A branch's heads also cancel and redo its trips and runs, cancel a ใบคุมรถ it made, send a bill standing there back to
 * its sender (`return@branch`: where it stands is their home branch), and undo a handover recorded there by mistake
 * (`undeliver@branch`)
 */
const branchMovesLead: Permission[] = [
  'tms.waybill:return@branch',
  'tms.waybill:undeliver@branch',
  'tms.manifest:cancel@branch',
  'tms.trip:cancel@branch',
  'tms.trip:amend@branch',
  'tms.deliveryRun:cancel@branch',
  'tms.deliveryRun:amend@branch',
]

/**
 * Who gives which role: HR any but those who see pay (payroll, the accountant who pays the runs, the manager who reads
 * everything): only a super admin gives those, or HR could give someone sight of pay it may not see itself. Transport
 * and drivers transport gives too: its heads, who alone invite people and approve sign-ups (`joining`)
 */
const byTransport = ['transport', 'hr']
const bySuperAdmin: string[] = []

/**
 * Payroll's records that are nobody's pay: the kinds of money, the trip and fuel rates, the settings, and salary advances (what was
 * asked for, decided by the heads of teams HR keeps). Every other payroll record is someone's pay — what they are
 * paid, extras and deductions, payslips, runs, the income brought forward on a tax declaration — and so is any added later
 */
const notPay = ['payroll.payItem', 'payroll.tripRate', 'payroll.fuelRate', 'payroll.payrollSetting', 'payroll.advance']
const payRecords = subjects.filter((s) => s.app === 'payroll' && !notPay.includes(s.subject))

/** Each action a resource offers that some permissions give, and which of its records it reaches */
export type Rights = Map<string, Map<string, PermissionScope>>

/**
 * What permissions give, resource by resource and action by action: read from the abilities they make (`defineAbility`,
 * as the API enforces them), so wildcards spread over what each resource offers, a narrower grant beside a wider one
 * counts as the wider, and `@own` on a record without an owner reaches all of them, as it does there. The roles page
 * shows a role by it
 */
export function rightsOf(permissions: readonly Permission[]): Rights {
  const probe = { id: 'probe:user', branchId: 'probe:branch', team: { employeeIds: ['probe:employee'], userIds: ['probe:teammate'] } }
  const ability = defineAbility(permissions, probe, subjects)
  const reachOf = (conditions: Record<string, unknown>): PermissionScope => {
    const values = Object.values(conditions)
    return values.includes(probe.branchId) ? 'branch' : values.some(isOneOf) ? 'team' : 'own'
  }
  const rights: Rights = new Map()
  for (const { subject, actions } of subjects) {
    for (const action of actions) {
      const scope = scopeFor(ability, action, subject)
      if (!scope) continue
      const reach = scope === 'all' ? 'all' : widest(scope.map(reachOf))
      rights.set(subject, (rights.get(subject) ?? new Map()).set(action, reach))
    }
  }
  return rights
}

const reachOrder: PermissionScope[] = ['own', 'team', 'branch', 'all']

/** The widest of reaches (a branch's is wider than a team's, which may span branches but is fewer people) */
const widest = (reaches: readonly PermissionScope[]): PermissionScope =>
  reaches.reduce((a, b) => (reachOrder.indexOf(b) > reachOrder.indexOf(a) ? b : a), 'own')

/** What a role gives its holders who head a team (`RoleTemplate.lead`), by its key: the code's, as every role is */
export const leadOf = (key: string): readonly Permission[] => defaultRoles.find((role) => role.key === key)?.lead ?? []

/**
 * What someone may do: what everyone may (`everyonePermissions`), what their roles carry, and — heading a team — what
 * every head does over theirs (`teamPermissions`) and their roles' `lead`. The API builds their ability with it and
 * sends it to the apps
 */
export function permissionsOf(roles: readonly { key: string; permissions: readonly Permission[] }[], heading: boolean): Permission[] {
  const lead = heading ? [...teamPermissions, ...roles.flatMap((role) => leadOf(role.key))] : []
  return [...new Set([...everyonePermissions, ...roles.flatMap((role) => role.permissions), ...lead])]
}

/** Whether permissions give everything in every app (`*`): a super admin's */
export const givesEverything = (permissions: readonly Permission[]) =>
  permissions.map(parsePermission).some((p) => p.subject === '*' && p.action === '*' && p.scope === 'all')

/**
 * Whether one may approve a bill, a refund or a pay run they put up or changed themselves: only a super admin, the owner
 * and the one approver, whose own would otherwise wait for nobody. Such an approval is marked (`doc.selfApproved`) and
 * logged as one; everyone else's waits for someone else
 */
export const approvesOwn = (permissions: readonly Permission[]) => givesEverything(permissions)

/**
 * Who may do one action on a resource: everyone signed in (`everyonePermissions`), every head of a team over it
 * (`teamPermissions`), and each role that gives it, how far — `lead` when the role gives it only to its holders who
 * head a team (`RoleTemplate.lead`), or further to them than to the others
 */
export interface Access<R> {
  everyone?: PermissionScope
  team?: PermissionScope
  roles: { role: R; reach: PermissionScope; lead?: true }[]
}

/**
 * Who may do what, asked resource by resource and action by action, from roles as the database or the code has them
 * (each read once with `rightsOf`; what a role gives heads of a team is the code's, by its key). Roles that give
 * everything (`givesEverything`) are left out: wherever it is shown, that they may do all of it is said once
 */
export function accessBy<R extends { key?: string; permissions: readonly Permission[] }>(roles: readonly R[]) {
  const everyone = rightsOf(everyonePermissions)
  const team = rightsOf(teamPermissions)
  const given = roles
    .filter((role) => !givesEverything(role.permissions))
    .map((role) => ({ role, rights: rightsOf(role.permissions), lead: rightsOf([...role.permissions, ...leadOf(role.key ?? '')]) }))
  return (subject: string, action: string): Access<R> => ({
    everyone: everyone.get(subject)?.get(action),
    team: team.get(subject)?.get(action),
    roles: given.flatMap(({ role, rights, lead }) => {
      const reach = rights.get(subject)?.get(action)
      const heading = lead.get(subject)?.get(action)
      if (heading && heading !== reach) return [{ role, reach: heading, lead: true as const }]
      return reach ? [{ role, reach }] : []
    }),
  })
}

/**
 * Whether these rights reach someone else's pay, by any action (reading it, exporting, printing, or changing it, which
 * shows it too). Only one's own records (a payslip `@own`) are not someone else's. A role that does is given only by a
 * super admin (`grantableBy` empty: a test holds every role to it), or whoever gives it could show pay they may not see
 * themselves
 */
export function seesPay(permissions: readonly Permission[]) {
  const rights = rightsOf(permissions)
  return payRecords.some((record) => [...(rights.get(record.subject)?.values() ?? [])].some((reach) => reach !== 'own'))
}

/**
 * Every role there is, and the only place one changes: nobody edits roles or their permissions in the apps (`admin.role`
 * is read only), and each start of the API makes the database match this list (`syncRoles` in apps/api/src/db/seed.ts)
 */
export const defaultRoles: RoleTemplate[] = [
  {
    key: 'super_admin',
    name: 'ผู้ดูแลระบบสูงสุด',
    description: 'ทำได้ทุกอย่างในทุกแอพ',
    permissions: ['*'],
    grantableBy: [],
  },
  {
    key: 'transport',
    name: 'ฝ่ายขนส่งสินค้า',
    description:
      'รับฝากพัสดุ ออกบิลงานขนส่งและใบเสร็จ ดูแลสมุดผู้ติดต่อ เปิดเที่ยวรถและรอบส่งของสาขาตัวเอง จ่ายงานเหมาคัน ดูแลรถและพนักงานขับรถ (คนที่มีลูกทีมยกเลิกและแก้ไขเอกสารของสาขาตัวเองได้ด้วย)',
    // The counter and dispatch in one: bills, receipts and refunds of their branch, the address book; charter jobs (paid
    // on receipts, a deposit when booking too), trucks and crews, which are no branch's; trips and runs from their branch
    permissions: [
      'tms.waybill:read',
      'tms.waybill:create',
      'tms.waybill:update@branch',
      'tms.waybill:delete@branch',
      'tms.waybill:submit@branch',
      'tms.waybill:print',
      'tms.contact:*',
      'tms.product:read',
      'tms.receipt:read',
      'tms.receipt:submit',
      'tms.receipt:print',
      // Asks for a refund of what a customer overpaid, and pays it back once a head of the branch approves
      'tms.refund:read',
      'tms.refund:create',
      'tms.refund:submit@branch',
      'tms.refund:pay@branch',
      'tms.refund:print',
      'tms.shipment:*',
      'tms.vehicle:*',
      'tms.driver:*',
      'accounting.customer:read',
      ...branchMoves,
      ...cashHandler,
    ],
    // A head of a team in transport is a head of their branch: undoes and redoes what it issued, decides its refunds,
    // looks after its things and lets people in there
    lead: [
      'tms.waybill:cancel@branch',
      'tms.waybill:amend@branch',
      'tms.waybill:export',
      'tms.receipt:update@branch',
      'tms.receipt:cancel@branch',
      'tms.receipt:amend@branch',
      'tms.receipt:export',
      'tms.refund:approve@branch',
      'tms.refund:cancel@branch',
      'tms.refund:export',
      ...branchMovesLead,
      ...assetLead,
      ...joining,
    ],
    grantableBy: byTransport,
  },
  {
    key: 'driver',
    name: 'พนักงานขับรถ',
    description: 'ดูและอัปเดตสถานะเฉพาะงานของตัวเองผ่านมือถือ ส่งของและเก็บเงินในรอบส่งของตัวเอง',
    // Drivers collect freight at the door and pay for fuel and tolls: cash in hand. Their own runs: delivering, and
    // taking the freight on the branch's receipt — no receipt rights of their own (`collect@own` reaches only bills out on
    // their run, the cash in their hands). Trips between branches are the branches' to scan: their drivers use no app
    permissions: [
      'tms.shipment:read@own',
      'tms.shipment:update@own',
      'tms.deliveryRun:read@own',
      'tms.deliveryRun:deliver@own',
      'tms.deliveryRun:collect@own',
      'tms.vehicle:read',
      ...cashHandler,
    ],
    grantableBy: byTransport,
  },
  {
    key: 'accountant',
    name: 'ฝ่ายบัญชี',
    description: 'ลูกค้า ใบแจ้งหนี้ และรายงานลูกหนี้ รับเงินบิลและงานเหมาคัน แก้และยกเลิกใบเสร็จได้ทุกสาขา',
    permissions: [
      // Everything in accounting but approving bills: a manager approves what accounting records (and then pays)
      'accounting.customer:*',
      'accounting.invoice:*',
      'accounting.billingNote:*',
      'accounting.creditNote:*',
      // A receipt in the customer's name in place of a counter one: accounting's alone, not the counter's or a lead's
      'accounting.customerReceipt:*',
      'accounting.payment:*',
      'accounting.account:*',
      'accounting.billPayment:*',
      'accounting.bill:read',
      'accounting.bill:create',
      'accounting.bill:update',
      'accounting.bill:delete',
      'accounting.bill:export',
      'accounting.bill:print',
      'accounting.bill:submit',
      'accounting.bill:cancel',
      'accounting.bill:amend',
      'tms.shipment:read',
      'tms.shipment:export',
      'tms.waybill:read',
      'tms.waybill:print',
      'tms.receipt:*',
      'tms.refund:*',
      // Every person's cash: issues and tops up reserves, reviews collections spent, verifies deposits, sets up the
      // safes (at which branch) and moves cash between accounts; approves expense claims and transfers them back
      'cash.*',
      // What the company's things are worth, and whether a damaged or lost one comes out of the person's deposit
      'company.asset:read',
      'company.asset:export',
      'company.assetHandover:read',
      'company.assetHandover:review',
      'company.assetHandover:print',
      'company.assetHandover:export',
      // Pays the approved runs and files their tax and social security
      'payroll.payrollRun:read',
      'payroll.payrollRun:export',
      'payroll.payrollRun:pay',
      'payroll.payslip:read',
      'payroll.payslip:export',
      'payroll.payslip:print',
      // Transfers the approved advances
      'payroll.advance:read',
      'payroll.advance:pay',
      'payroll.advance:export',
    ],
    grantableBy: bySuperAdmin,
  },
  {
    key: 'manager',
    name: 'ผู้จัดการ',
    description: 'ดู ส่งออก และพิมพ์ได้ทุกแอพ เสนอใบแจ้งหนี้',
    // Approves the pay runs payroll prepares (and reopens one before it is paid), and the bills accounting records
    permissions: [
      '*:read',
      '*:export',
      '*:print',
      'accounting.invoice:submit',
      'payroll.payrollRun:approve',
      'payroll.payrollRun:reopen',
      'accounting.bill:approve',
      'tms.refund:approve',
    ],
    grantableBy: bySuperAdmin,
  },
  {
    key: 'hr',
    name: 'ฝ่ายบุคคล (HR)',
    description:
      'พนักงาน แผนก ตำแหน่ง ข้อมูลส่วนตัว เวลาทำงาน ลิงก์เชิญ และอนุมัติคำขอทุกสาขา (ให้บทบาทอื่นได้ ยกเว้นผู้ดูแลระบบและบทบาทที่เห็นเงินเดือน: ฝ่ายเงินเดือน ฝ่ายบัญชี ผู้จัดการ)',
    // Often held together with another role (the accountant does HR): a user may have several. Pay is not HR's (Payroll)
    permissions: [
      'hr.*',
      'access.*',
      'company.branch:rotateCode',
      'tms.driver:read',
      // Every branch's things: the register, handing them out and taking them back (a loss's cost is accounting's)
      'company.asset:*',
      'company.assetHandover:read',
      'company.assetHandover:create',
      'company.assetHandover:update',
      'company.assetHandover:delete',
      'company.assetHandover:submit',
      'company.assetHandover:cancel',
      'company.assetHandover:amend',
      'company.assetHandover:print',
      'company.assetHandover:export',
      'company.uniform:*',
    ],
    grantableBy: [],
  },
  {
    key: 'payroll',
    name: 'ฝ่ายเงินเดือน',
    description: 'ค่าจ้าง งวดเงินเดือนและสลิป เบิกเงินล่วงหน้า ภาษีและประกันสังคม (ให้บทบาทนี้ได้เฉพาะผู้ดูแลระบบ)',
    // Pay is kept from HR: only a super admin gives this role. HR's records it works from are read only: who is
    // employed and where, and their time, leave and overtime to check a payslip against
    permissions: [
      'payroll.*',
      'hr.employee:read',
      'hr.department:read',
      'hr.position:read',
      'hr.timeEntry:read',
      'hr.timeEntry:export',
      'hr.leave:read',
      'hr.overtime:read',
      'hr.schedule:read',
      'hr.holiday:read',
      'hr.leaveType:read',
    ],
    grantableBy: bySuperAdmin,
  },
]
